import { memo } from 'react';
import { TabProps } from './props';
import styles from './styles.module.scss';
import { ToolTip } from '@starsoft/common/components';
import { useTranslation } from '../../../hooks';
import { SettingLayoutComponentId } from '../../../models';
import { SettingsService } from '@starsoft/common/services/SettingsService';

/**
 * Tab Component
 * A customizable tab button component that supports icons, translations, and different visual styles.
 * Features include active state indication, disabled state, tooltips for inactive tabs, and size variants.
 *
 * @component
 * @template T - Type for tab value
 * @param {Object} props - Component props
 * @param {Tab<T>} props.tab - Tab object containing label, value, icon and inactive state
 * @param {Function} props.setTab - Callback to handle tab selection
 * @param {boolean} props.isTiny - Whether to use tiny styling
 * @param {string} props.version - Version ID to control component appearance
 * @param {boolean} props.isSmall - Whether to use small styling
 * @param {boolean} props.disabled - Whether tab is disabled
 * @param {T} props.currentTab - Currently selected tab value
 * @param {boolean} props.useMaxContent - Whether to use max-content width
 * @param {string} props.i18nNamespace - i18n namespace for translations
 * @returns {JSX.Element} The rendered tab button component
 *
 * @example
 * interface TabData {
 *   id: string;
 *   name: string;
 * }
 *
 * const tabData = {
 *   label: 'settings.general',
 *   value: { id: '1', name: 'General' },
 *   icon: 'icon-settings',
 *   inactive: false
 * };
 *
 * <Tab<TabData>
 *   tab={tabData}
 *   currentTab={tabData.value}
 *   setTab={(value) => function('Selected:', value)}
 *   version={SettingLayoutComponentId.TabsV1.toString()}
 *   isSmall={false}
 *   isTiny={false}
 *   disabled={false}
 *   useMaxContent={false}
 *   i18nNamespace="settings"
 * />
 */
function Tab<T>({
  tab,
  setTab,
  isTiny,
  version,
  isSmall,
  disabled,
  currentTab,
  useMaxContent,
  i18nNamespace,
}: TabProps<T>) {
  const { t } = useTranslation(!!i18nNamespace ? i18nNamespace : 'common');
  const { t: tCommon } = useTranslation('common');
  const isActive = currentTab === tab?.value;

  /**
   * Handles tab click events
   * Only triggers setTab if the tab is not inactive
   * @returns {void}
   */
  function handleClick() {
    if (tab?.inactive) {
      return;
    }

    setTab(tab?.value);
  }

  return (
    <ToolTip
      visible={!!tab?.inactive}
      titleComponent={tCommon('option_unavailable')}
    >
      <button
        className={`${styles.button} ${isTiny ? styles['button--tiny'] : ''} ${isActive ? styles['button--active'] : ''} ${isSmall ? styles['button--small'] : ''} ${isActive && isSmall ? styles['button--small--active'] : ''} ${version === SettingLayoutComponentId.TabsV3?.toString() ? styles['button--container'] : ''} ${!useMaxContent ? styles['button--max-content'] : ''} ${version === SettingLayoutComponentId.TabsV1?.toString() ? styles['button--with-indicator'] : ''} ${tab?.inactive ? styles['button--disabled'] : ''}`}
        onClick={handleClick}
        type="button"
        id={`tab-button-${tab?.value}`}
        disabled={disabled}
      >
        <div className={styles.button__content}>
          {tab?.icon && <i className={tab?.icon} />}
          {t(tab?.label, {
            app_provider: SettingsService.env.PROVIDER,
          })}
        </div>
      </button>
    </ToolTip>
  );
}

export default memo(Tab) as typeof Tab;
